import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { CLP } from "@/lib/api";
import { PageContainer, PageHeader, StatusBadge, PrimaryButton } from "@/components/ui-kit";
import { computeFinanceKpis } from "@/lib/financeKpis";
import { Users, CalendarDays, TrendingUp, AlertCircle, ArrowUpRight, Plus, FileText, AlertTriangle, Clock, CheckCircle2, ArrowDownCircle } from "lucide-react";

function KpiCard({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-neutral-200 p-6 hover:border-[#0A0A0A] transition-colors">
      <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className={`font-mono-kpi font-bold text-3xl mt-3 ${accent || "text-[#0A0A0A]"}`}>{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-2">{sub}</div>}
    </div>
  );
}

function FinanceKpiLink({ to, label, value, count, icon, accent, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="bg-white border border-neutral-200 p-5 hover:border-[#FF5A00] transition-colors group flex flex-col"
    >
      <div className="flex items-center gap-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
        {icon}<span>{label}</span>
        <ArrowUpRight className="w-3 h-3 ml-auto text-neutral-300 group-hover:text-[#FF5A00]" />
      </div>
      <div className={`font-mono-kpi font-bold text-2xl mt-3 ${accent}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1 font-mono-kpi">{count} registros</div>
    </Link>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [financeKpis, setFinanceKpis] = useState(null);
  const [monthAcct, setMonthAcct] = useState(null); // Cobrado + Costos pagados del mes

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
    // Reutiliza la misma fuente y lógica del módulo Finanzas.
    api.get("/invoices").then(({ data }) => setFinanceKpis(computeFinanceKpis(data || [])));
    api.get(`/finance/accounting-summary?year=${y}&month=${m}`).then(({ data }) => setMonthAcct(data));
  }, []);

  if (!stats) {
    return (
      <PageContainer>
        <div data-testid="dashboard-loading" className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">
          Cargando…
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        kicker="Panel principal"
        title="Dashboard de Operación"
        subtitle="Indicadores clave de All Active en tiempo real."
        actions={
          <Link to="/admin/calendario" data-testid="dashboard-new-service">
            <PrimaryButton>
              <Plus className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />Nuevo servicio
            </PrimaryButton>
          </Link>
        }
      />

      {/* KPIs operacionales */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiCard
          testid="kpi-active-customers"
          label="Clientes activos"
          value={stats.active_customers}
          sub={`${stats.total_customers} en total`}
        />
        <KpiCard
          testid="kpi-services-month"
          label="Servicios este mes"
          value={stats.services_this_month}
          sub={`${stats.services_completed_month} completados`}
          accent="text-[#FF5A00]"
        />
        <KpiCard
          testid="kpi-revenue-month"
          label="Ingresos del mes"
          value={CLP(stats.revenue_month)}
          sub="Cobros pagados"
          accent="text-[#00C853]"
        />
      </div>

      {/* KPIs financieros (mismos cálculos que Finanzas) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <FinanceKpiLink
          testid="dashboard-kpi-deuda-facturada"
          to="/admin/finanzas?filter=deuda"
          label="Deuda facturada"
          value={CLP(financeKpis?.deuda_facturada.amount ?? 0)}
          count={financeKpis?.deuda_facturada.count ?? 0}
          icon={<FileText className="w-3.5 h-3.5 text-[#1E40AF]" />}
          accent="text-[#1E40AF]"
        />
        <FinanceKpiLink
          testid="dashboard-kpi-vencidos"
          to="/admin/finanzas?filter=vencido"
          label="Vencidos"
          value={CLP(financeKpis?.vencidos.amount ?? 0)}
          count={financeKpis?.vencidos.count ?? 0}
          icon={<AlertTriangle className="w-3.5 h-3.5 text-[#D50000]" />}
          accent="text-[#D50000]"
        />
        <FinanceKpiLink
          testid="dashboard-kpi-por-facturar"
          to="/admin/finanzas?filter=por_facturar"
          label="Por facturar"
          value={CLP(financeKpis?.por_facturar.amount ?? 0)}
          count={financeKpis?.por_facturar.count ?? 0}
          icon={<Clock className="w-3.5 h-3.5 text-neutral-500" />}
          accent="text-neutral-800"
        />
      </div>

      {/* KPIs del mes en curso — cobrado, costos, servicios pendientes */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <FinanceKpiLink
          testid="dashboard-kpi-cobrado-mes"
          to="/admin/finanzas?view=caja"
          label="Cobrado del mes"
          value={CLP(monthAcct?.cobrado?.gross ?? 0)}
          count={monthAcct?.cobrado?.count ?? 0}
          icon={<CheckCircle2 className="w-3.5 h-3.5 text-[#00C853]" />}
          accent="text-[#00C853]"
        />
        <FinanceKpiLink
          testid="dashboard-kpi-costos-pagados"
          to="/admin/finanzas?tab=cashflow"
          label="Costos pagados"
          value={CLP(monthAcct?.costos_pagados?.amount ?? 0)}
          count={monthAcct?.costos_pagados?.count ?? 0}
          icon={<ArrowDownCircle className="w-3.5 h-3.5 text-[#B71C1C]" />}
          accent="text-[#B71C1C]"
        />
        <FinanceKpiLink
          testid="dashboard-kpi-servicios-pendientes"
          to="/admin/calendario"
          label="Servicios pendientes"
          value={stats.upcoming_services ?? 0}
          count={stats.services_this_month ?? 0}
          icon={<CalendarDays className="w-3.5 h-3.5 text-[#FF5A00]" />}
          accent="text-[#FF5A00]"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming services */}
        <div className="lg:col-span-2 bg-white border border-neutral-200">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-4 h-4 text-[#FF5A00]" />
              <div className="font-display font-bold text-base">Próximos servicios (7 días)</div>
            </div>
            <Link
              to="/admin/calendario"
              data-testid="dashboard-view-calendar"
              className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FF5A00] inline-flex items-center gap-1"
            >
              Ver calendario <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-neutral-100">
            {stats.upcoming_list.length === 0 && (
              <div className="px-6 py-10 text-center text-sm text-neutral-500">
                No hay servicios programados en los próximos 7 días.
              </div>
            )}
            {stats.upcoming_list.map((s) => (
              <Link
                to={`/admin/servicios/${s.id}`}
                key={s.id}
                data-testid={`dashboard-upcoming-${s.id}`}
                className="block px-6 py-3 hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{s.customer_name}</div>
                    <div className="text-xs text-neutral-500 truncate">{s.customer_address}</div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="font-mono-kpi text-xs">{s.scheduled_date}</div>
                    <StatusBadge status={s.status} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Sidebar widgets */}
        <div className="space-y-4">
          <div className="bg-[#0A0A0A] text-white p-6">
            <TrendingUp className="w-5 h-5 text-[#FF5A00] mb-3" />
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-400">
              Tasa de completado
            </div>
            <div className="font-mono-kpi font-bold text-3xl mt-2">
              {stats.services_this_month > 0
                ? Math.round((stats.services_completed_month / stats.services_this_month) * 100)
                : 0}
              %
            </div>
            <div className="text-xs text-neutral-400 mt-2">
              {stats.services_completed_month} de {stats.services_this_month} servicios este mes
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-6">
            <AlertCircle className="w-5 h-5 text-[#D50000] mb-3" />
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              Próximas acciones
            </div>
            <ul className="mt-3 space-y-2 text-sm">
              <li className="flex items-center justify-between">
                <span className="text-neutral-600">Servicios programados</span>
                <span className="font-mono-kpi font-bold">{stats.upcoming_services}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-neutral-600">Deuda facturada</span>
                <span className="font-mono-kpi font-bold text-[#1E40AF]">{CLP(financeKpis?.deuda_facturada.amount ?? 0)}</span>
              </li>
              <li className="flex items-center justify-between">
                <span className="text-neutral-600">Vencido</span>
                <span className="font-mono-kpi font-bold text-[#D50000]">{CLP(financeKpis?.vencidos.amount ?? 0)}</span>
              </li>
            </ul>
          </div>

          <Link
            to="/admin/clientes"
            data-testid="dashboard-quick-customers"
            className="block bg-white border border-neutral-200 p-6 hover:border-[#FF5A00] transition-colors"
          >
            <Users className="w-5 h-5 text-[#FF5A00] mb-3" />
            <div className="font-display font-bold text-base">Gestionar clientes</div>
            <div className="text-xs text-neutral-500 mt-1">
              Crear, editar y revisar historial completo
            </div>
          </Link>
        </div>
      </div>
    </PageContainer>
  );
}
